// JSON-Schema → form state → tool arguments.
//
// The form keeps a parallel "draft" tree rather than the final JSON value so inputs can
// hold in-progress text (e.g. "-" while typing a number) and so "not set" is distinct
// from false/0/"". `toArguments` converts the draft into the request body, dropping
// unset optional fields and collecting per-field validation errors.

export type JsonSchema = {
  type?: string | string[]
  title?: string
  description?: string
  default?: unknown
  enum?: unknown[]
  properties?: Record<string, JsonSchema>
  required?: string[]
  items?: JsonSchema | JsonSchema[]
  format?: string
  [k: string]: unknown
}

/** Draft value held by the form. Leaves are strings (text/number/enum/json) or booleans. */
export type Draft = string | boolean | undefined | Draft[] | { [key: string]: Draft }

export type FieldKind = 'enum' | 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array' | 'json'

export type FieldErrors = Record<string, string>

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

export function asSchema(v: unknown): JsonSchema {
  return isPlainObject(v) ? (v as JsonSchema) : {}
}

/** First non-"null" type, so `["string", "null"]` renders as a string field. */
function primaryType(s: JsonSchema): string | undefined {
  if (Array.isArray(s.type)) return s.type.find((t) => t !== 'null')
  return s.type
}

export function kindOf(s: JsonSchema): FieldKind {
  if (Array.isArray(s.enum) && s.enum.length > 0) return 'enum'
  const t = primaryType(s)
  if (t === 'string' || t === 'number' || t === 'integer' || t === 'boolean') return t
  if (t === 'array') return isPlainObject(s.items) ? 'array' : 'json'
  if (t === 'object' || (t === undefined && isPlainObject(s.properties))) {
    return isPlainObject(s.properties) && Object.keys(s.properties).length > 0 ? 'object' : 'json'
  }
  return 'json'
}

export function propertiesOf(s: JsonSchema): [string, JsonSchema][] {
  return isPlainObject(s.properties) ? Object.entries(s.properties).map(([k, v]) => [k, asSchema(v)]) : []
}

export function requiredOf(s: JsonSchema): Set<string> {
  return new Set(Array.isArray(s.required) ? s.required.filter((r): r is string => typeof r === 'string') : [])
}

export function itemSchema(s: JsonSchema): JsonSchema {
  return asSchema(s.items)
}

/** Stable option value for an enum member (enums may hold numbers, booleans or null). */
export const enumKey = (v: unknown): string => JSON.stringify(v)

/**
 * Build a draft from a schema. `value` (e.g. a past run's arguments) wins over the
 * schema `default`; with neither, fields start unset.
 */
export function initialDraft(s: JsonSchema, value?: unknown): Draft {
  const v = value !== undefined ? value : s.default
  switch (kindOf(s)) {
    case 'enum':
      return v === undefined ? '' : enumKey(v)
    case 'string':
      return typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v)
    case 'number':
    case 'integer':
      return typeof v === 'number' ? String(v) : ''
    case 'boolean':
      return typeof v === 'boolean' ? v : undefined
    case 'object': {
      const src = isPlainObject(v) ? v : {}
      return Object.fromEntries(propertiesOf(s).map(([k, ps]) => [k, initialDraft(ps, src[k])]))
    }
    case 'array':
      return Array.isArray(v) ? v.map((item) => initialDraft(itemSchema(s), item)) : []
    case 'json':
      return v === undefined ? '' : JSON.stringify(v, null, 2)
  }
}

type Converted = { set: false } | { set: true; value: unknown }
const UNSET: Converted = { set: false }

function convert(s: JsonSchema, d: Draft, path: string, required: boolean, errors: FieldErrors): Converted {
  const missing = () => {
    if (required) errors[path] = 'Required'
    return UNSET
  }
  switch (kindOf(s)) {
    case 'enum': {
      if (typeof d !== 'string' || d === '') return missing()
      return { set: true, value: JSON.parse(d) }
    }
    case 'string': {
      if (typeof d !== 'string' || d === '') return missing()
      return { set: true, value: d }
    }
    case 'number':
    case 'integer': {
      if (typeof d !== 'string' || d.trim() === '') return missing()
      const n = Number(d)
      if (!Number.isFinite(n)) {
        errors[path] = 'Must be a number'
        return UNSET
      }
      if (kindOf(s) === 'integer' && !Number.isInteger(n)) {
        errors[path] = 'Must be a whole number'
        return UNSET
      }
      return { set: true, value: n }
    }
    case 'boolean': {
      if (typeof d === 'boolean') return { set: true, value: d }
      // A required boolean renders as a checkbox: unchecked means false.
      return required ? { set: true, value: false } : UNSET
    }
    case 'object': {
      const draft = isPlainObject(d) ? (d as Record<string, Draft>) : {}
      const req = requiredOf(s)
      const out: Record<string, unknown> = {}
      for (const [k, ps] of propertiesOf(s)) {
        const c = convert(ps, draft[k], path ? `${path}.${k}` : k, req.has(k), errors)
        if (c.set) out[k] = c.value
      }
      if (Object.keys(out).length === 0 && !required) return UNSET
      return { set: true, value: out }
    }
    case 'array': {
      const items = Array.isArray(d) ? d : []
      const out: unknown[] = []
      items.forEach((item, i) => {
        // Every added item is meant to be sent, so each one is required.
        const c = convert(itemSchema(s), item, `${path}[${i}]`, true, errors)
        if (c.set) out.push(c.value)
      })
      if (out.length === 0 && !required) return UNSET
      return { set: true, value: out }
    }
    case 'json': {
      if (typeof d !== 'string' || d.trim() === '') return missing()
      try {
        return { set: true, value: JSON.parse(d) }
      } catch {
        errors[path] = 'Invalid JSON'
        return UNSET
      }
    }
  }
}

/** Convert a root draft into the `arguments` object for POST /tools/{name}/execute. */
export function toArguments(root: JsonSchema, draft: Draft): { args: Record<string, unknown>; errors: FieldErrors } {
  const errors: FieldErrors = {}
  const schema: JsonSchema = kindOf(root) === 'object' ? root : { type: 'object', properties: {} }
  const c = convert(schema, draft, '', true, errors)
  const args = c.set && isPlainObject(c.value) ? c.value : {}
  return { args, errors }
}
